// Get started progress, shared by the page and the sidebar badge: one fetch, refreshed on
// focus and whenever a step is marked or a tour finishes.
import { useEffect, useState } from "react";
import { fetchWithAuth } from "../utils/authFetch";

const EVENT = "scoutm8:get-started";
let cache = null;
let inflight = null;

export function loadGetStarted({ fresh = false } = {}) {
  if (cache && !fresh) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchWithAuth("/api/get-started")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (body) {
          cache = body;
          window.dispatchEvent(new CustomEvent(EVENT, { detail: body }));
        }
        return cache;
      })
      .catch(() => cache)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

// Record a step that leaves no other trace ("research", "alerts"), or that the page was seen.
export async function markGetStarted(body) {
  try {
    await fetchWithAuth("/api/get-started", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    // Best effort: a missed mark only leaves a step unticked.
  } finally {
    loadGetStarted({ fresh: true });
  }
}

export function useGetStarted() {
  const [state, setState] = useState(cache);
  useEffect(() => {
    const on = (e) => setState(e.detail);
    const onFocus = () => loadGetStarted({ fresh: true });
    window.addEventListener(EVENT, on);
    window.addEventListener("focus", onFocus);
    loadGetStarted({ fresh: true });
    return () => {
      window.removeEventListener(EVENT, on);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return state;
}

// The free trial covers finding candidates only: the rest of Get started is locked, and not counted.
export const TRIAL_OPEN = new Set(["search", "results", "credits"]);
// The in-house edition has no jobs board, research, saved firms or job alerts: those steps don't exist for it.
export const INHOUSE_HIDDEN = new Set(["track", "research", "save", "alerts"]);
export function openOnPlan(done, onTrial, edition = null) {
  let open = onTrial ? Object.fromEntries(Object.entries(done).filter(([k]) => TRIAL_OPEN.has(k))) : done;
  if (edition === "inhouse") open = Object.fromEntries(Object.entries(open).filter(([k]) => !INHOUSE_HIDDEN.has(k)));
  return open;
}

// Every checklist item, done or not: server-side steps plus name, bookmarks, signature and prompts from user settings.
export function getStartedDone(progress, settings, extra = {}) {
  const d = progress?.done || {};
  return {
    name: Boolean(String(settings?.firstName || "").trim()),
    mailbox: Boolean(d.mailbox || extra.mailbox),
    track: Boolean(d.track),
    research: Boolean(d.research),
    search: Boolean(d.search),
    save: Boolean(settings?.watchlist?.length || settings?.companies?.length),
    outreach: Boolean(d.outreach),
    signature: Boolean(settings?.signatures?.length),
    prompts: Boolean(settings?.aiPrompts?.length),
    alerts: Boolean(d.alerts),
    results: Boolean(d.results),
    credits: Boolean(d.credits),
  };
}
